import { Panel, Label, Toast, Snackbar, ClickEvent, Table, Component, Textbox, BlurEvent, TableRow } from "@mcleod/components";
import { Api, HorizontalAlignment, LogManager, Alignment, ModelRow } from "@mcleod/core";
import { CommonTooltips, CommonDialogs } from "@mcleod/common";

const log = LogManager.getLogger("lme/datafusion/DataFusionUtil");

/**
 * Handles expandable panel toggle functionality with animation and button rotation
 * @param isExpanded Current expansion state of the panel
 * @param collapsiblePanel The panel component to expand/collapse
 * @param expandButton The button component to rotate
 * @param transitionProps Optional transition properties (defaults to { speed: 100, paddingTop: 0 })
 * @returns Promise<boolean> New expansion state
 */
export async function toggleExpandablePanel(
    isExpanded: boolean,
    collapsiblePanel: any,
    expandButton: any,
    transitionProps: { speed: number, paddingTop: number } = { speed: 100, paddingTop: 0 }
): Promise<boolean> {
    if (isExpanded) {
        await collapsiblePanel.collapse(transitionProps);
        expandButton.imageRotation = 90;
    } else {
        await collapsiblePanel.expand(transitionProps);
        expandButton.imageRotation = 0;
    }
    return !isExpanded;
}

/**
 * Updates label formatting and auto-expands/collapses panels based on table row counts
 * @param panels Array of panel configuration objects
 * @param forceCollapse Whether to force collapse all panels (used on initial load)
 */
export function updateXrefLabelFormatting(panels: Array<{
    table: any;
    label: any;
    isExpanded: boolean;
    collapsiblePanel: any;
    expandButton: any;
    onStateChange: (newState: boolean) => void;
}>, forceCollapse: boolean = false) {
    panels.forEach(panelConfig => {
        const hasRows = panelConfig.table.rowCount > 0;

        // Set label formatting first
        panelConfig.label.fontBold = hasRows;

        // Only collapse panels if forceCollapse is true (initial load)
        if (forceCollapse) {
            setTimeout(() => {
                panelConfig.collapsiblePanel.collapse({ speed: 100, paddingTop: 0 }).then(() => {
                    panelConfig.expandButton.imageRotation = 90;
                });
                panelConfig.onStateChange(false);
            }, 1);
        }
    });
}

/**
 * Gets the filtered row count for a table based on its custom filter
 * @param table The table to count filtered rows for
 * @returns The number of visible/filtered rows, or total row count if no filter is applied
 */
export function getFilteredRowCount(table: Table): number {
    if (!table || !table.customFilter) {
        return table ? table.rowCount : 0;
    }

    let visibleCount = 0;
    for (let i = 0; i < table.rowCount; i++) {
        const row = table.getRowByIndex(i);
        if (row && table.customFilter(row)) {
            visibleCount++;
        }
    }
    return visibleCount;
}

/**
 * Sets quick info tooltip for a component using the specified layout
 * @param value The value to display in the tooltip
 * @param component The component to attach the tooltip to
 * @param layout The layout to use for formatting the tooltip
 */
export function setQuickInfo(value: any, component: Component, layout: string) {
    CommonTooltips.setTooltipContentLayout(component, value, layout);
    component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
}

/**
 * Handles communication ID selection logic that can be reused across multiple layouts
 * @param field The field name being processed
 * @param textbox The textbox component that triggered the selection
 * @param hasSelection Whether there is a selection
 * @param activeRow The active data row to update
 */
export function handleCommIdSelection(field: string, textbox: Textbox, hasSelection: boolean, activeRow: ModelRow, transSet: string): void {
    if (hasSelection === true) {
        if (textbox.getFirstLookupModelData() != null) {
            const commType = textbox.getFirstLookupModelData()?.get("comm_type");
            const id = textbox.getFirstLookupModelData()?.get("id");

            // Determine which filename field to check and clear based on the field being processed
            let filenameField: string | null = null;
            let dialogTitle: string = "";

            switch (field) {
                case "reply_comm_id":
                    filenameField = "reply_filename";
                    dialogTitle = "Clear Reply Filename";
                    break;
                case "func_comm_id":
                    filenameField = "func_ack_filename";
                    dialogTitle = "Clear Functional Acknowledgment Filename";
                    break;
                case "edi_comm_id":
                    filenameField = "filename";
                    dialogTitle = "Clear Filename";
                    break;
            }

            // Check if the corresponding filename field is populated (regardless of comm type)
            if (filenameField) {
                const currentFilename = activeRow.get(filenameField);
                if (currentFilename && currentFilename.trim() !== "") {
                    CommonDialogs.showYesNo(
                        `The filename is currently populated. Do you want to clear it?`,
                        dialogTitle
                    ).then((result) => {
                        if (result) {
                            // User clicked Yes - clear the filename
                            activeRow.set(filenameField, "");
                        }
                        // Only generate new filename from API if comm type is "A" and we have an ID
                        if ("A" === commType && id) {
                            generateFilenameFromApi(field, activeRow, id, transSet);
                        }
                    });
                    return; // Exit early to wait for user response
                } else {
                    // Filename field is empty, generate directly if comm type is "A"
                    if ("A" === commType && id) {
                        generateFilenameFromApi(field, activeRow, id, transSet);
                    }
                }
            }
        }
    }
}

/**
 * Helper function to make the API call and set the appropriate filename field
 */
function generateFilenameFromApi(field: string, activeRow: ModelRow, commId: string, transSet: string): void {
    Api.post("lme/datafusion/generate-as2-file-mask", { comm_id: commId, transaction_set: transSet }).then(result => {
        if (field && result && result.data[0].file_mask) {
            switch (field) {
                case "reply_comm_id":
                    activeRow.set("reply_filename", result.data[0].file_mask);
                    break;
                case "func_comm_id":
                    activeRow.set("func_ack_filename", result.data[0].file_mask);
                    break;
                case "edi_comm_id":
                    activeRow.set("filename", result.data[0].file_mask);
                    break;
            }
        }
    });
}

export async function onBlurCrossReference(event: BlurEvent, table: Table, field: string) {
    const textbox = event.target as Textbox;
    const newTableRow = TableRow.getContainingTableRow(textbox);
    const newRow = newTableRow.data as ModelRow;
    const newText = textbox.text;
    if (newText) {
        const existingRows = table.data;
        const isDuplicate = existingRows.find((row) =>
            row.get(field) === newText && row !== newRow
        );
        if (isDuplicate) {
            // Show error message to user
            const yesNo = await CommonDialogs.showYesNo(`The Partner Code "${newText}" already exists in the table, are you sure you want to add another?`);
            if (!yesNo) {
                textbox.text = null;
            }
        }
    }
}

export class DataFusionUtil {

    static async isCancelAllowed(carrierId: string, movementId: string): Promise<boolean> {
        const results = await this.areCancelsAllowed([{ carrierId, movementId }]);
        return results.length > 0 ? results[0].allowed : false;
    }

    static async areCancelsAllowed(carrierMoves: { carrierId: string, movementId: string }[]): Promise<{ carrierId: string, movementId: string, allowed: boolean }[]> {
        const response = await Api.search("lme/datafusion/df-cancel-allowed", carrierMoves);

        const results = response?.data?.map(result => {
            const carrierMove = carrierMoves.find(move => move.movementId === result.movement_id);
            const cancelAllowed = !!result?.is_cancel_allowed;
            if (!!result?.information) {
                log.debug("Cancel tender not sent: " + result.information);
            }
            return {
                ...carrierMove,
                allowed: cancelAllowed
            };
        });

        return results || [];
    }

    static sendCancelTender(carrierId: string, moveId: string, showToast: boolean): Promise<{ movementId: string, data: any }> {
        return Api.post("lme/datafusion/send-tender", {
            carrier: carrierId, move_id: moveId, respond_by: null, purpose: "CANCEL", contacts: null
        }).then(response => {
            if (showToast) {
                if (response.data?.[0]?.tendersent ?? false) {
                    const text = response.data[0].text;
                    const batch = response.data[0].batchid;
                    const p = new Panel();
                    p.add(new Label({ text: "Carrier was Successfully Removed", align: HorizontalAlignment.CENTER, fontBold: true, fontSize: "xxlarge" }));
                    const labelText = ((batch == "0") ? text : "\t  CANCEL tender sent in batch " + batch).trim();
                    p.add(new Label({ fontBold: true, fontSize: "large", align: HorizontalAlignment.CENTER, text: labelText }));
                    Toast.showToast(p, { backgroundColor: "primary" });
                } else {
                    Snackbar.showWarningSnackbar("Unable to send CANCEL tender for movement " + moveId + ".  " + response.data[0].text);
                }
            }
            return { movementId: moveId, data: response.data?.[0] };
        });
    }
}
